// @givmo/social — Accounts: the pages and accounts the organization posts to (SMM-WEB1; §SMM-8
// point 2, "Connected accounts").
//
// A row per connection (its mark, its name, its platform, its state): connected, or needing a
// reconnect (with Reconnect), or connected with a date by which to reconnect (`reconnect_by`, a
// token the platform ends). One Connect per grant family the store's offered-providers read lists
// — none for a family it does not list, so no button goes nowhere. The custody acts (Connect,
// Reconnect, Disconnect) only for a holder of `social:manage_connections`, fail-closed: a Member
// sees the accounts and none of the acts.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sdk, type OrgIntegrationConnectionOut } from '@givmo/api-client';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorMessage,
  RowActions,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { canManageConnections, type SocialConfig } from './social-config';
import { startConnect, type ConnectStart } from './social-connect';
import { PlatformMark } from './platform-mark';
import { grantFamilyLabel, grantFamilyOf, platformLabel } from './social-platforms';
import {
  useConnections,
  useOfferedFamilies,
  useRefreshSocial,
  useScheduledCount,
  type OfferedFamily,
} from './social-queries';
import { SOCIAL_PAGE_TEST_ID } from './social-routes';
import { SocialLoading, SocialPageShell, SocialRefused, dayWords } from './social-ui';
import { socialWords as w } from './social-words';

function accountName(row: OrgIntegrationConnectionOut): string {
  return row.account_name?.trim() || w.unnamedAccount;
}

/** One line under the account's name: its platform, then its state. */
function stateLine(row: OrgIntegrationConnectionOut): { text: string; attention: boolean } {
  if (row.status === 'needs_reconnect') return { text: w.needsReconnect, attention: true };
  if (row.reconnect_by)
    return { text: w.reconnectBy(dayWords(row.reconnect_by)), attention: false };
  return { text: w.connectedOn(dayWords(row.connected_at)), attention: false };
}

export function AccountsPage({ config }: { config: SocialConfig }) {
  const { orgRef } = config;
  const manager = canManageConnections(config);
  const connections = useConnections(orgRef);
  const offered = useOfferedFamilies(manager ? orgRef : null);
  const [starting, setStarting] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<OrgIntegrationConnectionOut | null>(null);

  const begin = (key: string, start: ConnectStart) => {
    if (orgRef === null) return;
    setStarting(key);
    setStartError(null);
    void startConnect(orgRef, start).then((refusal) => {
      if (refusal) {
        setStarting(null);
        setStartError(refusal);
      }
    });
  };

  const families = offered.data ?? [];
  const offeredFamily = (provider: string) =>
    families.find((entry) => entry.family === grantFamilyOf(provider)) ?? null;

  return (
    <SocialPageShell
      title={w.accountsTitle}
      description={w.accountsLede}
      testId={SOCIAL_PAGE_TEST_ID}
    >
      {orgRef === null || connections.isPending ? (
        <SocialLoading>{w.loadingAccounts}</SocialLoading>
      ) : connections.isError ? (
        <SocialRefused error={connections.error} onRetry={() => void connections.refetch()} />
      ) : (
        <>
          {startError ? (
            <ErrorMessage testId="social-connect-start-error">{startError}</ErrorMessage>
          ) : null}
          <ConnectedCard
            rows={connections.data}
            manager={manager}
            starting={starting}
            onReconnect={(row) => {
              const family = offeredFamily(row.provider);
              if (family === null) return;
              begin(row.connection_id, {
                kind: 'reconnect',
                connectionId: row.connection_id,
                provider: row.provider,
                family: family.family,
              });
            }}
            canReconnect={(row) => offeredFamily(row.provider) !== null}
            onDisconnect={setDisconnecting}
          />
          {manager ? (
            <AddCard
              families={families}
              pending={offered.isPending}
              starting={starting}
              onConnect={(family) =>
                begin(family.family, {
                  kind: 'connect',
                  family: family.family,
                  providers: family.providers,
                })
              }
            />
          ) : null}
        </>
      )}
      {disconnecting !== null && orgRef !== null ? (
        <DisconnectDialog
          orgRef={orgRef}
          account={disconnecting}
          onClose={() => setDisconnecting(null)}
        />
      ) : null}
    </SocialPageShell>
  );
}

function ConnectedCard({
  rows,
  manager,
  starting,
  onReconnect,
  canReconnect,
  onDisconnect,
}: {
  rows: readonly OrgIntegrationConnectionOut[];
  manager: boolean;
  starting: string | null;
  onReconnect: (row: OrgIntegrationConnectionOut) => void;
  canReconnect: (row: OrgIntegrationConnectionOut) => boolean;
  onDisconnect: (row: OrgIntegrationConnectionOut) => void;
}) {
  if (rows.length === 0) {
    return (
      <Card className="px-24 py-32" testId="social-accounts-empty">
        <EmptyState
          title={w.noAccountsTitle}
          description={manager ? w.noAccountsManager : w.noAccountsMember}
        />
      </Card>
    );
  }
  return (
    <Card testId="social-accounts">
      <h2 className="px-24 pt-20 pb-4 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
        {w.connectedHeading}
      </h2>
      <ul className="divide-y divide-border-subtle">
        {rows.map((row) => {
          const state = stateLine(row);
          const name = accountName(row);
          return (
            <li
              key={row.connection_id}
              className="flex items-center gap-12 px-24 py-14"
              {...testIdProp(`social-account-${row.connection_id}`)}
            >
              <PlatformMark platform={row.provider} size={28} />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                  {name}
                </span>
                <span className="font-sans text-s13 tracking-givmo text-text-secondary">
                  {platformLabel(row.provider)}
                  {' · '}
                  <span
                    className={state.attention ? 'text-warning-700' : undefined}
                    {...testIdProp(`social-account-${row.connection_id}-state`)}
                  >
                    {state.text}
                  </span>
                </span>
              </div>
              {manager && row.status === 'needs_reconnect' && canReconnect(row) ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={starting === row.connection_id}
                  onClick={() => onReconnect(row)}
                  testId={`social-account-${row.connection_id}-reconnect`}
                >
                  {w.reconnect}
                </Button>
              ) : null}
              {manager ? (
                <RowActions
                  label={w.rowActions(name)}
                  actions={[
                    {
                      label: w.disconnect,
                      destructive: true,
                      onSelect: () => onDisconnect(row),
                      testId: `social-account-${row.connection_id}-disconnect`,
                    },
                  ]}
                  testId={`social-account-${row.connection_id}-actions`}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function AddCard({
  families,
  pending,
  starting,
  onConnect,
}: {
  families: readonly OfferedFamily[];
  pending: boolean;
  starting: string | null;
  onConnect: (family: OfferedFamily) => void;
}) {
  if (pending) return null;
  return (
    <Card testId="social-add-account">
      <h2 className="px-24 pt-20 pb-4 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
        {w.addHeading}
      </h2>
      {families.length === 0 ? (
        <p
          className="px-24 pb-20 font-sans text-s13 tracking-givmo text-text-secondary"
          {...testIdProp('social-none-offered')}
        >
          {w.noneOffered}
        </p>
      ) : (
        <>
          <p
            className="px-24 pb-8 font-sans text-s13 tracking-givmo text-text-secondary"
            {...testIdProp('social-what-givmo-does')}
          >
            {w.whatGivmoDoes}
          </p>
          <ul className="divide-y divide-border-subtle">
            {families.map((family) => (
              <li
                key={family.family}
                className="flex items-center gap-12 px-24 py-14"
                {...testIdProp(`social-family-${family.family}`)}
              >
                <span className="flex w-[52px] shrink-0 items-center gap-4">
                  {family.providers.map((provider) => (
                    <PlatformMark key={provider} platform={provider} size={24} />
                  ))}
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {grantFamilyLabel(family.family, family.providers)}
                  </span>
                  <span className="font-sans text-s13 tracking-givmo text-text-secondary">
                    {w.familyLine(family.family)}
                  </span>
                  {family.family === 'meta' && family.providers.includes('instagram') ? (
                    <span
                      className="font-sans text-s12 tracking-givmo text-text-tertiary"
                      {...testIdProp('social-instagram-hint')}
                    >
                      {w.instagramHint}
                    </span>
                  ) : null}
                </div>
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={starting === family.family}
                  onClick={() => onConnect(family)}
                  testId={`social-connect-${family.family}`}
                >
                  {w.connect}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function DisconnectDialog({
  orgRef,
  account,
  onClose,
}: {
  orgRef: string;
  account: OrgIntegrationConnectionOut;
  onClose: () => void;
}) {
  const refresh = useRefreshSocial(orgRef);
  const name = accountName(account);
  const platform = platformLabel(account.provider);
  const scheduled = useScheduledCount(orgRef, account.connection_id);
  const disconnect = useMutation({
    mutationFn: async () => {
      await sdk.orgIntegrationsDisconnectOrgIntegration({
        path: { org_ref: orgRef, connection_id: account.connection_id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.disconnected(name));
      await refresh();
      onClose();
    },
  });
  const meta = account.provider === 'facebook_pages' || account.provider === 'instagram';
  const failed = disconnect.isError ? w.disconnectFailed : null;
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={w.disconnectTitle(name, platform)}
        width={448}
        className="flex flex-col gap-16"
        testId="social-disconnect-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
          {w.disconnectTitle(name, platform)}
        </Dialog.Title>
        <p className="font-sans text-s14 tracking-givmo text-text-secondary">
          {w.disconnectBody(platform)}
        </p>
        {scheduled.data ? (
          <p
            className={
              scheduled.data.count > 0
                ? 'font-sans text-s14 font-w500 tracking-givmo text-warning-700'
                : 'font-sans text-s14 tracking-givmo text-text-secondary'
            }
            {...testIdProp('social-disconnect-scheduled')}
          >
            {w.scheduledToIt(scheduled.data.count, scheduled.data.capped)}
          </p>
        ) : null}
        {meta ? (
          <p className="font-sans text-s13 tracking-givmo text-text-secondary">{w.removeAtMeta}</p>
        ) : account.provider === 'linkedin' ? (
          <p className="font-sans text-s13 tracking-givmo text-text-secondary">
            {w.removeAtLinkedInBefore}
            <a
              href={w.linkedInPermittedServices}
              target="_blank"
              rel="noreferrer"
              className="text-brand-600 underline-offset-2 hover:underline"
            >
              {w.permittedServices}
            </a>
            {w.removeAtLinkedInAfter}
          </p>
        ) : null}
        {failed ? <ErrorMessage testId="social-disconnect-error">{failed}</ErrorMessage> : null}
        <div className="flex justify-end gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="social-disconnect-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={disconnect.isPending}
            onClick={() => disconnect.mutate()}
            testId="social-disconnect-confirm"
          >
            {w.disconnectConfirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
